import { getFunctions, httpsCallable } from 'firebase/functions'
import {
  AggregationData,
  Collection,
  CollectionName,
  CopyTemplateToWorkspaceProps,
  CreateExternalClientProps,
  CreateExternalClientResponse,
  CreateLeadSessionsProps,
  DBProps,
  DCTable,
  DeleteExternalClientProps,
  DeleteLeadSessionsProps,
  DeleteUnusedContentProps,
  DeleteUnusedContentResponse,
  EventAISummaryProps,
  EventSmartColumnsUpdateProps,
  FontPaginationParams,
  FontPaginationResponse,
  GetAggregationProps,
  GetContentReferencesProps,
  GetDocsProps,
  GetDocumentReferencesProps,
  GetExternalClientsProps,
  GetLeadProps,
  GetLeadSessionProps,
  GetLeadSessionsProps,
  GetLeadsProps,
  GetPublicEventDataProps,
  IContentReference,
  IExternalClient,
  IPublicEvent,
  ITemplate,
  IntegrationLeadFieldCallProps,
  IntegrationLeadPushProps,
  InviteDataProps,
  Lead,
  LeadSession,
  LeadWithSessions,
  LookupValueDocProps,
  QRCodeConfiguration,
  QRCodeDataRequest,
  ReCaptchaVerificationProps,
  SessionWithLead,
  SiteUrlProps,
  TimezoneCallProps,
  UpdateExternalClientProps,
  UpdateLeadSessionsProps,
  UpdateLeadsProps,
  UpdateWorkspacePropertiesProps,
  UserWorkspaceStatus,
  UserWorkspaceStatusProps,
  WorkpsaceFontCallParams,
} from 'types'
import firebaseApp from './firebase'

const functions = getFunctions(firebaseApp)

export const acceptInvite = httpsCallable<InviteDataProps>(functions, 'acceptInvite')

export const getImageFromSiteUrl = httpsCallable<SiteUrlProps, string>(
  functions,
  'getImageFromSiteUrl'
)

export const getEventTimezone = httpsCallable<TimezoneCallProps, string>(
  functions,
  'getEventTimezone'
)

export const getPublicEventData = httpsCallable<GetPublicEventDataProps, IPublicEvent>(
  functions,
  'getPublicEventData'
)

export const verifyReCaptchaToken = httpsCallable<ReCaptchaVerificationProps, boolean>(
  functions,
  'verifyReCaptchaToken'
)

export const testQrConnection = httpsCallable<QRCodeConfiguration>(functions, 'testQrConnection')

export const getQrInfo = httpsCallable<QRCodeDataRequest, Record<string, string>>(
  functions,
  'getQrInfo'
)

export const getCRMLeadFields = httpsCallable<
  IntegrationLeadFieldCallProps,
  Record<string, string>
>(functions, 'getCRMLeadFields')

export const pushCRMLeads = httpsCallable<IntegrationLeadPushProps>(functions, 'pushCRMLeads')

export const getDocs = <T extends CollectionName, F extends keyof Collection[T]>(
  props: GetDocsProps<T, F>
) =>
  httpsCallable<GetDocsProps<T, F>, Pick<Collection[T], F | keyof DBProps>[]>(
    functions,
    'getDocs'
  )(props)

export const updateEventSmartValues = httpsCallable<EventSmartColumnsUpdateProps>(
  functions,
  'updateEventSmartValues'
)

export const cleanupLookupValueDocs = httpsCallable<LookupValueDocProps>(
  functions,
  'cleanupLookupValueDocs'
)

export const sendInvite = httpsCallable<InviteDataProps>(functions, 'sendInvite')

export const userWorkspaceStatus = httpsCallable<UserWorkspaceStatusProps, UserWorkspaceStatus>(
  functions,
  'userWorkspaceStatus'
)

export const getMomentifyFonts = httpsCallable<FontPaginationParams, FontPaginationResponse>(
  functions,
  'getMomentifyFonts'
)

export const getWorkspaceFontPreview = httpsCallable<WorkpsaceFontCallParams, string>(
  functions,
  'getWorkspaceFontPreview'
)

export const copyTemplateToWorkspace = httpsCallable<CopyTemplateToWorkspaceProps, ITemplate>(
  functions,
  'copyTemplateToWorkspace'
)

export const getLeadSession = httpsCallable<GetLeadSessionProps, SessionWithLead>(
  functions,
  'getLeadSession'
)

export const getLeadSessions = <PopulateLead extends boolean = false>(
  props: GetLeadSessionsProps<PopulateLead>
) =>
  httpsCallable<
    GetLeadSessionsProps<PopulateLead>,
    PopulateLead extends true ? SessionWithLead[] : Array<LeadSession & DBProps>
  >(
    functions,
    'getLeadSessions'
  )(props)

export const getLead = <T extends boolean = false>(props: GetLeadProps<T>) =>
  httpsCallable<GetLeadProps<T>, T extends true ? LeadWithSessions : Lead & DBProps>(
    functions,
    'getLead'
  )(props)

export const getLeads = <P extends boolean = false>(props: GetLeadsProps<P>) =>
  httpsCallable<GetLeadsProps<P>, P extends true ? LeadWithSessions[] : Array<Lead & DBProps>>(
    functions,
    'getLeads'
  )(props)

export const getAggregations = httpsCallable<GetAggregationProps, AggregationData[]>(
  functions,
  'getAggregations'
)

export const createLeadSessions = httpsCallable<CreateLeadSessionsProps, SessionWithLead[]>(
  functions,
  'createLeadSessions'
)

export const updateLeads = httpsCallable<UpdateLeadsProps, DCTable['leads'][]>(
  functions,
  'updateLeads'
)
export const updateLeadSessions = httpsCallable<UpdateLeadSessionsProps, DCTable['leadSessions'][]>(
  functions,
  'updateLeadSessions'
)

export const deleteLeadSessions = httpsCallable<DeleteLeadSessionsProps, void>(
  functions,
  'deleteLeadSessions'
)

export const summaryGenerator = httpsCallable<EventAISummaryProps, void>(
  functions,
  'summaryGenerator'
)

export const deleteUnusedContent = httpsCallable<
  DeleteUnusedContentProps,
  DeleteUnusedContentResponse
>(functions, 'deleteUnusedContent')

export const getContentReferences = httpsCallable<GetContentReferencesProps, IContentReference[]>(
  functions,
  'getContentReferences'
)

export const getDocumentReferences = httpsCallable<GetDocumentReferencesProps, IContentReference[]>(
  functions,
  'getDocumentReferences'
)

export const getExternalClients = httpsCallable<
  GetExternalClientsProps,
  Omit<IExternalClient, 'clientSecretHash'>[]
>(functions, 'getExternalClients')

export const createExternalClient = httpsCallable<
  CreateExternalClientProps,
  CreateExternalClientResponse
>(functions, 'createExternalClient')

export const updateExternalClient = httpsCallable<UpdateExternalClientProps, void>(
  functions,
  'updateExternalClient'
)

export const deleteExternalClient = httpsCallable<DeleteExternalClientProps, void>(
  functions,
  'deleteExternalClient'
)

export const updateWorkspaceProperties = httpsCallable<UpdateWorkspacePropertiesProps, void>(
  functions,
  'updateWorkspaceProperties'
)
